import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { Menu, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { counterpartPath } from "@/i18n/routes";

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const navigate = useNavigate();

  const navItems = t("nav.items");

  const handleLangToggle = () => {
    const nextLang = lang === "es" ? "en" : "es";
    const target = counterpartPath(loc.pathname, nextLang);
    setLang(nextLang);
    if (target !== loc.pathname) navigate(target);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream-100/95 backdrop-blur-sm border-b border-cream-400"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-16 sm:h-20 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-10" aria-label={lang === "es" ? "Navegación principal" : "Main navigation"}>
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-link-${item.href.slice(1)}`}
              className="text-[13px] uppercase tracking-[0.22em] text-olive-800 hover:text-gold-deep transition-colors duration-300"
            >
              {item.label}
            </a>
          ))}
          <a
            href={lang === "es" ? "/blog" : "/journal"}
            data-testid="nav-link-journal"
            className="text-[13px] uppercase tracking-[0.22em] text-olive-800 hover:text-gold-deep transition-colors duration-300"
          >
            {t("nav_ecom.journal")}
          </a>
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            type="button"
            onClick={handleLangToggle}
            data-testid="nav-lang-toggle"
            aria-label={t("nav.lang_switch_aria")}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-[0.24em] text-olive-800 border border-olive-700/30 hover:border-olive-700 hover:text-olive-900 transition-colors duration-200"
          >
            <span className={lang === "es" ? "text-olive-900 font-medium" : "text-olive-800/50"}>ES</span>
            <span aria-hidden="true" className="text-olive-800/40">/</span>
            <span className={lang === "en" ? "text-olive-900 font-medium" : "text-olive-800/50"}>EN</span>
          </button>
          <a
            href="/tienda"
            data-testid="nav-comprar-button"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.24em] hover:bg-olive-800 transition-colors duration-300"
          >
            {t("nav.cta")}
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-olive-800"
            aria-label={t("nav.open_menu")}
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-cream-100 border-t border-cream-400"
        >
          <nav className="flex flex-col px-6 py-6 gap-4">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.22em] text-olive-800 py-1"
              >
                {item.label}
              </a>
            ))}
            <a
              href={lang === "es" ? "/blog" : "/journal"}
              onClick={() => setOpen(false)}
              className="text-sm uppercase tracking-[0.22em] text-olive-800 py-1"
            >
              {t("nav_ecom.journal")}
            </a>
            <a
              href="/tienda"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex w-fit px-5 py-2.5 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.24em]"
            >
              {t("nav.cta")}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
